<template>
	<app-modal title="Go offline" @close="$emit('close')">
		<div>
			<div class="flex relative">
				<div class="px-3 w-96 overflow-y-auto">
					<p class="text-13">
						Why closing?
					</p>
					<div class="mt-3">
						<app-radio-group name="chooseReason" :options="closingReasons" v-model="closingReason.id" />
						<app-text-area
							name="Notes"
							container-class="mt-5"
							v-if="closingReason.id === 0"
							placeholder="Notes"
							v-model="closingReason.notes"
						/>
					</div>
				</div>
				<transition name="expand-horizontally">
					<div
						v-if="closingReason.id || closingReason.notes"
						class="border-l-default border-border-color px-5 w-96 overflow-y-auto"
					>
						<p class="text-13">
							Chose Opening time
						</p>
						<div>
							<app-select
								:options="closingTimeOptions"
								:can-deselect="false"
								v-model="closingTime"
								@select="setClosingTime"
							/>
							<div class="flex flex-row gap-2">
								<app-datepicker
									v-if="closingTime === 'OTHER'"
									placeholder="Date"
									:initial-value="endDate"
									@selected="(date) => (endDate = date)"
									:lowerLimit="today"
								/>
								<app-input
									name="time"
									v-if="closingTime === 'OTHER'"
									placeholder="Time"
									type="time"
									v-model="endTime"
								/>
							</div>
							<div class="flex flex-col mt-2">
								<div class="flex flex-row gap-2">
									<app-datepicker
										v-if="closingTime === 'CUSTOM'"
										placeholder="Start Date"
										:initial-value="startDate"
										@selected="
											(date) => {
												startDate = date;
											}
										"
										:lowerLimit="today"
									/>
									<app-input
										name="time"
										v-if="closingTime === 'CUSTOM'"
										placeholder="Start Time"
										type="time"
										v-model="startTime"
										@change="validateDateTime"
									/>
								</div>
								<div class="flex flex-row gap-2">
									<app-datepicker
										v-if="closingTime === 'CUSTOM'"
										placeholder="End Date"
										:initial-value="endDate"
										@selected="
											(date) => {
												endDate = date;
												validateDateTime();
											}
										"
										:lowerLimit="today"
									/>
									<app-input
										name="time"
										v-if="closingTime === 'CUSTOM'"
										placeholder="End Time"
										type="time"
										v-model="endTime"
										@change="validateDateTime"
									/>
								</div>
							</div>
						</div>
					</div>
				</transition>
			</div>
			<div class="flex justify-center mt-3">
				<app-button
					class="w-48 btn btn-primary"
					v-if="(closingReason.id || closingReason.notes) && closingTime && endDate && endTime"
					@click="goOffline"
				>
					Go offline
				</app-button>
			</div>
		</div>
	</app-modal>
</template>

<script>
	import AppModal from '@/components/app/AppModal.vue';
	import AppRadioGroup from '@/components/app/AppRadioGroup.vue';
	import AppButton from '@/components/app/AppButton.vue';
	import AppDatepicker from '@/components/app/AppDatepicker.vue';
	import moment from 'moment';
	import { mapGetters } from 'vuex';
	import BusinessService from '@/services/sales/BusinessService';
	import { debounce } from 'debounce';

	export default {
		name: 'GoOfflineModal',
		emits: ['close'],
		components: { AppDatepicker, AppButton, AppRadioGroup, AppModal },
		computed: {
			...mapGetters({ closingReasonsGlobal: 'closingReasonsGlobal' }),
		},
		props: {
			businessId: {
				type: Number,
				required: true,
			},
			cityId: {
				type: Number,
				required: true,
			},
		},
		data: () => ({
			closingReasons: [],
			closingReason: {
				id: 0,
				notes: '',
			},
			closingTimeOptions: [
				{ label: '30m', value: 30 },
				{ label: '1h', value: 60 },
				{ label: '1h 30m', value: 90 },
				{ label: '2h', value: 120 },
				{ label: '4h', value: 240 },
				{ label: '8h', value: 480 },
				{ label: '12h', value: 720 },
				{ label: '24h', value: 1440 },
				{ label: 'Other', value: 'OTHER' },
				{ label: 'Custom', value: 'CUSTOM' },
				{ label: 'Undefined', value: 'UNDEFINED' },
			],
			endDate: moment().format('YYYY-MM-DD'),
			endTime: moment().format('HH:mm:ss'),
			startTime: moment().format('HH:mm:ss'),
			startDate: moment().format('YYYY-MM-DD'),
			today: new Date(),
			closingTime: 0,
		}),
		methods: {
			validateDateTime: debounce(function() {
				const currentDateTime = moment();
				const startDateTime = moment(`${this.startDate} ${this.startTime}`, 'YYYY-MM-DD HH:mm:ss');
				const endDateTime = moment(`${this.endDate} ${this.endTime}`, 'YYYY-MM-DD HH:mm:ss');
				if (startDateTime.isBefore(currentDateTime)) {
					this.$notificationService.error('Start time must be after the current time.');
					return false;
				}
				if (endDateTime.isSameOrBefore(startDateTime)) {
					this.$notificationService.error('End time must be after the start time.');
					return false;
				}
				return true;
			}, 300),
			setClosingTime() {
				if (this.closingTime === 'UNDEFINED') {
					this.endDate = '2100-01-01';
					this.endTime = '00:00:00';
				}
				if (this.closingTime === 'OTHER') {
					this.endDate = moment().format('YYYY-MM-DD');
					this.endTime = moment().format('HH:mm:ss');
				}
				if (typeof this.closingTime === 'number') {
					let time = moment()
						.add(this.closingTime, 'minutes')
						.format('YYYY-MM-DD HH:mm:ss');
					this.endDate = moment(time).format('YYYY-MM-DD');
					this.endTime = moment(time).format('HH:mm:ss');
				}
			},
			goOffline() {
				if (this.closingTime === 'CUSTOM' && !this.validateDateTime()) {
					return;
				}
				this.emitter.emit('isLoading', true);
				let payload = {
					reason: this.closingReason.id,
					notes: this.closingReason.notes,
					endDate: `${this.endDate} ${this.endTime}`,
				};
				if (this.closingTime === 'CUSTOM') {
					payload.startDate = `${this.startDate} ${this.startTime}`;
				}
				BusinessService.setBusinessOffline(this.businessId, this.cityId, payload)
					.then(() => {
						let data = { ...payload };
						data.id = 1;
						data.reason = this.closingReasons
							? this.closingReasons.find((r) => r.id === this.closingReason.id).label
							: '';
						if (data.startDate >= moment().format('YYYY-MM-DD HH:mm:ss')) {
							this.$notificationService.success('Closing time set');
						} else {
							this.$store.commit('SET_BUSINESS_OFFLINE', this.businessId);
							this.$notificationService.success('Business is now offline');
						}
						this.$store.commit('SET_BUSINESS_CLOSING_TIME', { businessId: this.businessId, ...data });
						this.$emit('close');
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.emitter.emit('isLoading', false);
					});
			},
		},
		created() {
			if (!this.closingReasonsGlobal?.length) {
				this.$store
					.dispatch('setBusinessClosingReasons')
					.then((res) => {
						this.closingReasons = res;
					})
					.catch((e) => this.$notificationService.error(e));
			} else {
				this.closingReasons = this.closingReasonsGlobal;
			}
		},
	};
</script>

<style scoped></style>
